import TextField, { TextFieldProps } from '@mui/material/TextField';
import clsx from "clsx";
import React, { useState } from 'react';
import { Controller, useFormContext } from "react-hook-form";

import { INPUT_SIZES, INPUT_VARIANTS } from '../../utils/constants';
import { SystemTooltip } from "../ui/Tooltip";
import { FormDescription } from './shared';

import { FaEye, FaEyeSlash } from "react-icons/fa";

type TextInputProps = TextFieldProps & {
    label?: string;
    description?: string;
    inputSize?: keyof typeof INPUT_SIZES;

    tooltip?: string;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    inputVariant?: keyof typeof INPUT_VARIANTS;
}

export const SystemTextInput: React.FC<TextInputProps> = (props) => {
    const {
        name,
        hidden,
        description,
        className,
        leftIcon,
        rightIcon,
        tooltip,
        inputSize = "",
        inputVariant = 'gray_text_white_bg',
        required = false,
        autoComplete = "off",
        autoFocus = false,
        ...restOfProps
    } = props;

    return (
        <SystemTooltip message={tooltip}>
            <div
                className={clsx(
                    hidden && 'hidden',
                    className
                )}
            >
                {description && <FormDescription description={description} required={required} />}

                <TextField
                    {...restOfProps}
                    name={name}
                    className={clsx(
                        "w-full",
                        INPUT_VARIANTS[inputVariant],
                    )}
                    slotProps={{
                        input: {
                            endAdornment: rightIcon,
                            startAdornment: leftIcon,
                        }
                    }}
                    size='small'
                    autoComplete={autoComplete}
                    autoFocus={autoFocus}
                />
            </div>
        </SystemTooltip>
    );
}

type ControlledTextInputProps = TextInputProps & {
    name: string;
}

export const ControlledSystemTextInput: React.FC<ControlledTextInputProps> = (props) => {
    const { name, ...restOfProps } = props;
    const { control, formState } = useFormContext();

    return (
        <Controller
            name={name}
            control={control}
            render={({ field }) => (
                <SystemTextInput
                    {...field}
                    {...restOfProps}
                    error={Boolean(formState?.errors?.[name]?.message)}
                    helperText={formState?.errors?.[name]?.message as string}
                />
            )}
        />
    );
}

export const SystemPasswordInput: React.FC<TextInputProps> = (props) => {
    const { ...restOfProps } = props;

    const [showPassword, setShowPassword] = useState(false);

    return (
        <SystemTextInput
            {...restOfProps}
            type={showPassword ? "text" : "password"}
            rightIcon={showPassword ? <FaEye size={20} className='cursor-pointer' onClick={() => setShowPassword(false)} /> : <FaEyeSlash size={20} className='cursor-pointer' onClick={() => setShowPassword(true)} />}
        />
    );
}

export const ControlledSystemPasswordInput: React.FC<ControlledTextInputProps> = (props) => {
    const { name, helperText, ...restOfProps } = props;
    const { control, formState } = useFormContext();

    return (
        <Controller
            name={name}
            control={control}
            render={({ field }) => (
                <SystemPasswordInput
                    {...field}
                    {...restOfProps}
                    error={Boolean(formState?.errors?.[name]?.message)}
                    helperText={formState?.errors?.[name]?.message as string}
                />
            )}
        />
    );
}